import { useCallback, useRef } from 'react';

export const useSound = () => {
  const audioContextRef = useRef<AudioContext | null>(null);

  const initAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);

  // Option 1: Play a simple notification beep
  const playNotificationBeep = useCallback(async () => {
    try {
      const audioContext = initAudioContext();
      
      // Create a pleasant notification sound (ascending notes)
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(1000, audioContext.currentTime + 0.1);
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.warn('Could not play notification sound:', error);
    }
  }, [initAudioContext]);

  // Option 2: Play a sound file (you'll need to add the audio file to your public folder)
  const playNotificationSound = useCallback(async (soundFile: string = '/sounds/notification.mp3') => {
    try {
      const audio = new Audio(soundFile);
      audio.volume = 0.5;
      await audio.play();
    } catch (error) {
      console.warn('Could not play notification sound:', error);
      // Fallback to beep if file doesn't exist
      playNotificationBeep();
    }
  }, [playNotificationBeep]);

  // Option 3: Play a success sound
  const playSuccessSound = useCallback(async () => {
    try {
      const audioContext = initAudioContext();
      
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Happy ascending chord progression
      oscillator.frequency.setValueAtTime(523, audioContext.currentTime); // C5
      oscillator.frequency.setValueAtTime(659, audioContext.currentTime + 0.1); // E5
      oscillator.frequency.setValueAtTime(784, audioContext.currentTime + 0.2); // G5
      
      gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.4);
    } catch (error) {
      console.warn('Could not play success sound:', error);
    }
  }, [initAudioContext]);

  return {
    playNotificationBeep,
    playNotificationSound,
    playSuccessSound
  };
};